import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetCustomer } from "@/api";
import { useOrderDetail } from "@/hooks/useOrderDetail";
import { formatPersianDateMonthDay } from "@/lib/format/persian";

const STATUS_FLOW = ["created", "confirmed", "preparing", "shipped", "completed"] as const;

function formatOrderDate(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const datePart = formatPersianDateMonthDay(date);
  const timePart = new Intl.DateTimeFormat("fa-IR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${datePart} · ساعت ${timePart}`;
}

function formatTimelineDate(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const datePart = formatPersianDateMonthDay(date);
  const timePart = new Intl.DateTimeFormat("fa-IR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${datePart} · ${timePart}`;
}

function formatOrderDateShort(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return formatPersianDateMonthDay(date);
}

function formatCustomerSince(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    month: "long",
    year: "numeric",
  }).format(date);
}

export type OrderDetailTimelineEntry = {
  title: string;
  time: string;
  state: "done" | "current" | "upcoming";
};

export function useOrderDetailPage() {
  const { t } = useTranslation(["orders", "home", "common"]);
  const { orderId } = useParams({ from: "/_guard/orders/$orderId" });
  const id = Number(orderId);

  const { order, paymentLink, isLoading, isError, isUpdatingStatus, updateStatus } =
    useOrderDetail(id);

  const { data: customerResponse } = useGetCustomer(order?.customer_id ?? 0, {
    query: { enabled: Boolean(order?.customer_id) },
  });

  const customer = customerResponse?.data?.data;

  const timeline = useMemo<OrderDetailTimelineEntry[]>(() => {
    if (!order) return [];
    const history = [...order.status_history].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
    const currentStatus = order.status;
    const isCanceled = currentStatus === "canceled";

    const buildUpcoming = (afterStatus: string): OrderDetailTimelineEntry[] => {
      if (isCanceled) return [];
      const idx = STATUS_FLOW.indexOf(afterStatus as (typeof STATUS_FLOW)[number]);
      if (idx === -1) return [];
      return STATUS_FLOW.slice(idx + 1).map((status) => ({
        title: t(`home:orders.status.${status}`),
        time: "—",
        state: "upcoming" as const,
      }));
    };

    if (history.length === 0) {
      return [
        {
          title: t(`home:orders.status.${order.status}`),
          time: formatTimelineDate(order.created_at),
          state: isCanceled ? ("done" as const) : ("current" as const),
        },
        ...buildUpcoming(currentStatus),
      ];
    }

    const historyEntries: OrderDetailTimelineEntry[] = history.map((entry, index) => {
      const isLast = index === history.length - 1;
      const isCurrent = entry.new_status === currentStatus && isLast;
      return {
        title: t(`home:orders.status.${entry.new_status}`),
        time: formatTimelineDate(entry.created_at),
        state: isCurrent ? ("current" as const) : ("done" as const),
      };
    });

    const lastStatus = history[history.length - 1]?.new_status ?? currentStatus;
    return [...historyEntries, ...buildUpcoming(lastStatus)];
  }, [order, t]);

  return {
    order,
    customer,
    paymentLink,
    timeline,
    isLoading,
    isError,
    isUpdatingStatus,
    updateStatus,
    formatOrderDate,
    formatOrderDateShort,
    formatCustomerSince,
  };
}
